import { useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import type { SmbHomeImageStatus } from "@shared/smb-home-images";

type Slot = { binding: string; status: SmbHomeImageStatus; error: string | null; url: string | null; provenance: string };
const busy = (slot: Slot) => ["planned", "prompt-submitting", "prompt-ready", "render-submitting", "submitted"].includes(slot.status);
const labels: Record<SmbHomeImageStatus, string> = {
  planned: "Waiting for saved Home copy", blocked: "Configuration needed",
  "prompt-submitting": "Writing image prompt", "prompt-ready": "Prompt saved",
  "render-submitting": "Submitting image", submitted: "Recovering accepted image",
  ready: "Ready", failed: "Failed", ambiguous: "Operator inspection needed",
};

/** Independent progress remains available when the owning page failed but its
 * copy is still usable. Neither status polling nor reload submits new work. */
export function SmbHomeImageProgress({ brandId, pageId, onRefresh }: {
  brandId: string; pageId: string; onRefresh: () => void;
}) {
  const origin = `${brandId}:${pageId}`;
  const originRef = useRef(origin);
  originRef.current = origin;
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;
  const url = `/api/brands/${brandId}/lp-express/smb-home/${pageId}/images`;
  const query = useQuery<{ slots: Slot[] }>({
    queryKey: [url], queryFn: async () => {
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Could not load saved image progress.");
      const data = await response.json();
      if (!Array.isArray(data?.slots)) throw new Error("Invalid saved image progress response.");
      return data;
    },
    refetchInterval: q => q.state.data?.slots?.some(busy) ? 4000 : false,
  });
  const previous = useRef<{ origin: string; states: string }>();
  const states = JSON.stringify(query.data?.slots?.map(slot => [slot.binding, slot.status, slot.url, slot.error]));
  useEffect(() => {
    const old = previous.current;
    previous.current = { origin, states };
    // Refresh the canonical page only at a terminal/result change, not every
    // polling tick. Do not reset unsaved editor text during ordinary progress.
    if (old?.origin === origin && old.states !== states &&
        query.data?.slots.some(slot => slot.status === "ready" || slot.status === "failed" || slot.status === "blocked" || slot.status === "ambiguous"))
      refreshRef.current();
  }, [origin, states, query.data]);
  const retry = useMutation({
    mutationFn: async (slot: Slot) => {
      await apiRequest("POST", `${url}/${encodeURIComponent(slot.binding)}/retry`, {});
    },
    onSuccess: () => {
      if (originRef.current !== origin) return;
      void query.refetch();
      refreshRef.current();
    },
  });
  if (query.isError) return <p role="alert">Image progress is unavailable. Reload to check the saved slots.</p>;
  if (!query.data?.slots.length) return null;
  return (
    <section aria-label="Home image generation" className="space-y-3 rounded-lg border p-4" data-testid="smb-home-image-progress">
      <h3 className="font-medium">Home images</h3>
      <p className="text-sm text-muted-foreground">Generated images are illustrations, not verified photos of this business. Supplied photos and logos are preserved.</p>
      <ul className="space-y-3" aria-live="polite">
        {query.data.slots.map(slot => (
          <li key={slot.binding} className="flex items-start gap-3">
            {slot.url && <img src={slot.url} alt="AI-generated service illustration" className="h-16 w-20 rounded object-cover" />}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{slot.binding} · {labels[slot.status]}</p>
              {slot.status === "ready" && <p className="text-xs text-muted-foreground">{slot.provenance}</p>}
              {slot.error && <p className="text-sm" role="status">{slot.error}</p>}
              {["blocked", "failed", "submitted"].includes(slot.status) && (
                <Button type="button" size="sm" variant="outline" disabled={retry.isPending}
                  onClick={() => retry.mutate(slot)}>
                  {slot.status === "submitted" ? "Check existing render" : "Retry this image"}
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {retry.isError && <p role="alert">{retry.error.message || "This image cannot be retried right now."}</p>}
    </section>
  );
}
